import type {
  AppendixCellRefStatus,
  AppendixCodeItemOption,
  AppendixColumnDataType,
  AppendixDetail,
  AppendixReferenceCodeItem,
  AppendixReferencePosition,
  AppendixReferenceTargets,
  AppendixReferenceValue,
  CreateAppendixPayload,
} from '../types/codeIndex'
import { extractCompositeOptionFragment } from './compositeCodeUtils'

// Draft shapes used while an appendix table is being authored. Cells are keyed by column key so
// reordering or removing a column never shifts values between columns.
// A code item column points at a code item by its domain id (codeItemKey). For a composite code
// item it also names the subgroup (codeItemGroup) and its 0-based position range. Its cells hold
// the domain id of the chosen value; until the column has a code item they hold plain text, which
// is matched to a value once one is chosen. codeItemCode is the code sent back as codeItemData and
// is never displayed: what a column or cell shows always comes from the saved reference targets.
export type AppendixColumnDraft = {
  key: string
  // Saved ids and fields the editor does not expose, sent back on update.
  id?: string | null
  length?: number | null
  label: string
  dataType: AppendixColumnDataType
  unit: string
  codeItemCode: string
  codeItemKey?: string | null
  codeItemGroup?: string | null
  codeItemPosition?: number | null
  codeItemPositionEnd?: number | null
}

export type AppendixCellMeta = {
  id: string | null
  notes: string | null
  // The value as loaded, so a reference that was already broken does not block saving other edits.
  savedValue?: string
}

export type AppendixRowDraft = {
  key: string
  id?: string | null
  cells: Record<string, string>
  cellMeta?: Record<string, AppendixCellMeta>
}

// Saved appendix fields kept as they were loaded when an existing appendix is updated.
export type SavedAppendixTableMeta = {
  description: string
  tableId: string | null
  tableDescription: string
  definesValidCombinations?: boolean
}

// The code item a column points at, with the subgroup position range of a composite code item.
export type AppendixColumnRef = Pick<AppendixColumnDraft, 'codeItemKey' | 'codeItemGroup' | 'codeItemPosition' | 'codeItemPositionEnd'>

export const NO_COLUMN_REF: Required<AppendixColumnRef> = {
  codeItemKey: null,
  codeItemGroup: null,
  codeItemPosition: null,
  codeItemPositionEnd: null,
}

export const APPENDIX_COLUMN_TYPE_OPTIONS: Array<{ value: AppendixColumnDataType; label: string }> = [
  { value: 'Text', label: 'Text' },
  { value: 'Number', label: 'Number' },
  { value: 'CodeItem', label: 'Code item' },
]

export const NUMBER_CELL_ERROR = 'This column holds numbers. Enter a number, or change the column to text.'
export const BOOLEAN_CELL_ERROR = 'This column holds Yes or No values. Choose Yes or No.'
export const EMPTY_CELL_ERROR = 'Every cell needs a value.'
export const MISSING_VALUE_LABEL = 'No longer exists'
export const MISMATCH_VALUE_LABEL = 'Doesn\'t fit this column'

const NUMBER_PATTERN = /^-?\d+(\.\d+)?$/

export function getColumnTypeLabel(dataType: AppendixColumnDataType): string {
  return APPENDIX_COLUMN_TYPE_OPTIONS.find(option => option.value === dataType)?.label || dataType
}

// Lowercase noun used in sentences ("Turndown is now a number column").
export function getColumnTypeNoun(dataType: AppendixColumnDataType): string {
  return getColumnTypeLabel(dataType).toLowerCase()
}

export function columnHasUnit(dataType: AppendixColumnDataType): boolean {
  return dataType === 'Number' || dataType === 'String'
}

let draftKeySequence = 0

export function createDraftKey(prefix: string): string {
  draftKeySequence += 1
  return `${prefix}-${Date.now().toString(36)}-${draftKeySequence}`
}

export function createColumnDraft(): AppendixColumnDraft {
  return { key: createDraftKey('column'), label: '', dataType: 'Text', unit: '', codeItemCode: '', ...NO_COLUMN_REF }
}

export function createRowDraft(columns: AppendixColumnDraft[]): AppendixRowDraft {
  return {
    key: createDraftKey('row'),
    cells: Object.fromEntries(columns.map(column => [column.key, ''])),
  }
}

// Whether the column's cells hold value ids (rather than text).
export function columnHoldsRefs(column: Pick<AppendixColumnDraft, 'dataType'> & AppendixColumnRef): boolean {
  return column.dataType === 'CodeItem' && Boolean(column.codeItemKey)
}

// -----------------------------
// Picker options, built from the saved reference targets
// -----------------------------

// The part of a composite option code at a position range ("1***" at 0 is "1"). Null when the
// value has no usable fragment there.
function valueFragment(optionCode: string, start: number, end: number): string | null {
  const fragment = extractCompositeOptionFragment(optionCode, start, end)
  return fragment && fragment.length === end - start + 1 && !fragment.includes('*') ? fragment : null
}

function rangeId(codeItemKey: string, group: string | null, start: number, end: number): string {
  return JSON.stringify([codeItemKey, group, start, end])
}

// A value fits a column when the column names no position (a simple code item), or when it
// belongs to the same subgroup and exactly the same position range.
export function valueFitsColumn(
  value: Pick<AppendixReferenceValue, 'group' | 'valuePosition' | 'valuePositionEnd'>,
  column: AppendixColumnRef
): boolean {
  if (column.codeItemPosition === null || column.codeItemPosition === undefined) return true
  return (value.group ?? null) === (column.codeItemGroup ?? null)
    && value.valuePosition === column.codeItemPosition
    && (value.valuePositionEnd ?? value.valuePosition) === (column.codeItemPositionEnd ?? column.codeItemPosition)
}

// The position ranges a composite code item offers: the server's list, or for an older server the
// distinct ranges its values fill.
function referencePositions(codeItem: AppendixReferenceCodeItem): AppendixReferencePosition[] {
  if (codeItem.positions) return codeItem.positions
  const ranges: AppendixReferencePosition[] = []
  codeItem.values.forEach(value => {
    if (value.valuePosition === null) return
    const position = value.valuePosition
    const positionEnd = value.valuePositionEnd ?? position
    if (positionEnd < position || valueFragment(value.optionCode, position, positionEnd) === null) return
    if (!ranges.some(range => range.group === value.group && range.position === position && range.positionEnd === positionEnd)) {
      ranges.push({ group: value.group, position, positionEnd })
    }
  })
  return ranges
}

// Picker options for code item columns. A composite code item (values carry a position) is not
// selectable itself: it lists one option per distinct subgroup position range, whose values show
// their fragment at that range. Every option's values hold the value key the cells store.
export function buildAppendixCodeItemOptions(targets: AppendixReferenceTargets | null): AppendixCodeItemOption[] {
  if (!targets) return []
  return targets.codeItems.map(codeItem => {
    const ranges = new Map<string, { group: string | null; start: number; end: number }>()
    referencePositions(codeItem).forEach(range => {
      const id = rangeId(codeItem.key, range.group, range.position, range.positionEnd)
      if (!ranges.has(id)) ranges.set(id, { group: range.group, start: range.position, end: range.positionEnd })
    })

    const positions: AppendixCodeItemOption[] = Array.from(ranges.entries())
      .sort(([, a], [, b]) => a.start - b.start || a.end - b.end || (a.group ?? '').localeCompare(b.group ?? ''))
      .map(([id, range]) => {
        const column = { codeItemGroup: range.group, codeItemPosition: range.start, codeItemPositionEnd: range.end }
        return {
          id,
          key: codeItem.key,
          code: codeItem.code,
          title: range.group ?? '',
          ...(range.group !== null ? { group: range.group } : {}),
          position: range.start,
          positionEnd: range.end,
          values: codeItem.values
            .filter(value => valueFitsColumn(value, column))
            .map(value => ({
              id: value.key,
              key: value.key,
              code: value.fragment || (valueFragment(value.optionCode, range.start, range.end) ?? value.optionCode),
              description: value.description,
            })),
        }
      })

    return {
      id: codeItem.key,
      key: codeItem.key,
      code: codeItem.code,
      title: codeItem.title,
      ...(positions.length > 0 ? { positions } : {}),
      values: codeItem.values.map(value => ({ id: value.key, key: value.key, code: value.optionCode, description: value.description })),
    }
  })
}

export function findCodeItemOption(
  codeItems: AppendixCodeItemOption[],
  reference: string | (Pick<AppendixColumnDraft, 'codeItemCode'> & AppendixColumnRef) | null | undefined
): AppendixCodeItemOption | null {
  if (!reference) return null
  const all = codeItems.flatMap(item => [item, ...(item.positions || [])])
  if (typeof reference === 'string') return all.find(item => codeItemChoice(item) === reference || item.code === reference) || null
  return all.find(item => (
    (reference.codeItemKey ? item.key === reference.codeItemKey : item.code === reference.codeItemCode)
    && (item.position ?? null) === (reference.codeItemPosition ?? null)
    && (item.positionEnd ?? null) === (reference.codeItemPositionEnd ?? reference.codeItemPosition ?? null)
    && (item.group ?? null) === (reference.codeItemGroup ?? null)
  )) || null
}

export function codeItemChoice(item: AppendixCodeItemOption): string {
  return item.position === undefined ? item.key || item.code : item.id
}

export function formatCodeItemTarget(item: AppendixCodeItemOption, includeCode = true): string {
  if (item.position === undefined) return `${includeCode ? `${item.code} — ` : ''}${item.title}`
  const end = item.positionEnd ?? item.position
  const position = end === item.position
    ? `Position ${item.position + 1}`
    : `Positions ${item.position + 1}–${end + 1}`
  return `${includeCode ? `Code item ${item.code} — ` : ''}Subgroup ${item.group || item.title} · ${position} within code item`
}

// The column reference a picked option stands for. A composite code item itself names no position
// and leaves the column without a usable reference until a position is picked.
export function columnRefFromOption(option: AppendixCodeItemOption | null): Required<AppendixColumnRef> & { codeItemCode: string } {
  if (!option) return { ...NO_COLUMN_REF, codeItemCode: '' }
  return {
    codeItemCode: option.code,
    codeItemKey: option.key || null,
    codeItemGroup: option.group ?? null,
    codeItemPosition: option.position ?? null,
    codeItemPositionEnd: option.position === undefined ? null : option.positionEnd ?? option.position,
  }
}

// -----------------------------
// Reference index: what code items and values currently say
// -----------------------------

export type AppendixRefCodeItemLabel = {
  key: string
  code: string
  number: string
  title: string
  // Whether the code item's values carry positions (built from live targets only).
  composite: boolean
  // Only for entries taken from a server-resolved appendix (no live targets).
  status?: 'Resolved' | 'PositionOutOfRange'
}

export type AppendixRefValueLabel = {
  key: string
  // From live targets the whole option code; from a server-resolved appendix what the cell shows
  // (already the fragment on a composite code item).
  optionCode: string
  // What the cell shows for this value (live targets only).
  fragment?: string
  description: string
  // Null for entries taken from a server-resolved appendix (no live targets).
  codeItemKey: string | null
  group: string | null
  valuePosition: number | null
  valuePositionEnd: number | null
  status?: AppendixCellRefStatus
}

export type AppendixRefIndex = {
  // True when built from the saved reference targets, which decide every status. Otherwise the
  // statuses the server resolved on the loaded appendix are used (e.g. a frozen revision).
  live: boolean
  codeItems: Map<string, AppendixRefCodeItemLabel>
  values: Map<string, AppendixRefValueLabel>
  // Position ranges each composite code item offers, as `rangeId`s (live only).
  ranges: Set<string>
}

export function buildRefLabelIndex(
  targets: AppendixReferenceTargets | null,
  detail?: AppendixDetail | null
): AppendixRefIndex {
  const codeItems = new Map<string, AppendixRefCodeItemLabel>()
  const values = new Map<string, AppendixRefValueLabel>()
  const ranges = new Set<string>()

  if (targets) {
    targets.codeItems.forEach(codeItem => {
      codeItems.set(codeItem.key, {
        key: codeItem.key,
        code: codeItem.code,
        number: codeItem.number,
        title: codeItem.title,
        composite: codeItem.values.some(value => value.valuePosition !== null),
      })
      referencePositions(codeItem).forEach(range => ranges.add(rangeId(codeItem.key, range.group, range.position, range.positionEnd)))
      codeItem.values.forEach(value => {
        values.set(value.key, {
          key: value.key,
          optionCode: value.optionCode,
          fragment: value.fragment,
          description: value.description,
          codeItemKey: codeItem.key,
          group: value.group,
          valuePosition: value.valuePosition,
          valuePositionEnd: value.valuePositionEnd,
        })
      })
    })
    return { live: true, codeItems, values, ranges }
  }

  detail?.tables.forEach(table => {
    table.columns.forEach(column => {
      const ref = column.codeItemRef
      if (!column.codeItemKey || !ref || ref.status === 'Missing' || ref.status === 'NotApplicable') return
      codeItems.set(column.codeItemKey, {
        key: column.codeItemKey,
        code: ref.code ?? '',
        number: ref.number ?? '',
        title: ref.title ?? '',
        composite: false,
        status: ref.status,
      })
    })
    table.rows.forEach(row => row.cells.forEach(cell => {
      const ref = cell.valueRef
      if (!cell.valueRefId || !ref || ref.status === 'Missing' || ref.status === 'Empty' || ref.optionCode === null) return
      values.set(cell.valueRefId, {
        key: cell.valueRefId,
        optionCode: ref.optionCode,
        description: ref.description ?? '',
        codeItemKey: null,
        group: null,
        valuePosition: null,
        valuePositionEnd: null,
        status: ref.status,
      })
    }))
  })
  return { live: false, codeItems, values, ranges }
}

export const EMPTY_REF_INDEX: AppendixRefIndex = buildRefLabelIndex({ codeItems: [] })

export type AppendixCellRefState = {
  status: AppendixCellRefStatus
  // Current text of the value (its fragment at the column's position range for a composite code
  // item); never the text it was picked with.
  optionCode: string | null
  description: string | null
}

const EMPTY_CELL_STATE: AppendixCellRefState = { status: 'Empty', optionCode: null, description: null }

// What a value shows in a column: the fragment at the column's position range when the column
// names one, otherwise the whole option code. The server already resolves the fragment on a loaded
// appendix, so only live targets are cut here.
function cellText(label: AppendixRefValueLabel, column: AppendixColumnRef, live: boolean): string {
  const start = column.codeItemPosition
  if (!live || start === null || start === undefined || label.valuePosition === null) return label.optionCode
  if (label.fragment && label.valuePosition === start) return label.fragment
  return valueFragment(label.optionCode, start, column.codeItemPositionEnd ?? start) ?? label.optionCode
}

export function getCellRefState(
  column: Pick<AppendixColumnDraft, 'dataType'> & AppendixColumnRef,
  value: string | null | undefined,
  index: AppendixRefIndex
): AppendixCellRefState {
  const key = (value ?? '').trim()
  if (!key || !columnHoldsRefs(column)) return EMPTY_CELL_STATE

  const label = index.values.get(key)
  if (!label) return { status: 'Missing', optionCode: null, description: null }

  const fits = index.live
    ? label.codeItemKey === column.codeItemKey && valueFitsColumn(label, column)
    : label.status !== 'Mismatch'
  return { status: fits ? 'Resolved' : 'Mismatch', optionCode: cellText(label, column, index.live), description: label.description }
}

export type AppendixColumnRefState =
  | { status: 'NotApplicable' | 'Unset' | 'Missing'; codeItem: null }
  | { status: 'Resolved' | 'PositionOutOfRange'; codeItem: AppendixRefCodeItemLabel }

// A composite code item needs the column to name one of the position ranges its values use; a
// simple code item takes no position.
export function getColumnRefState(
  column: Pick<AppendixColumnDraft, 'dataType'> & AppendixColumnRef,
  index: AppendixRefIndex
): AppendixColumnRefState {
  if (column.dataType !== 'CodeItem') return { status: 'NotApplicable', codeItem: null }
  if (!column.codeItemKey) return { status: 'Unset', codeItem: null }

  const codeItem = index.codeItems.get(column.codeItemKey)
  if (!codeItem) return { status: 'Missing', codeItem: null }

  if (!index.live) return { status: codeItem.status === 'PositionOutOfRange' ? 'PositionOutOfRange' : 'Resolved', codeItem }

  const start = column.codeItemPosition ?? null
  const fits = codeItem.composite
    ? start !== null && index.ranges.has(rangeId(codeItem.key, column.codeItemGroup ?? null, start, column.codeItemPositionEnd ?? start))
    : start === null
  return { status: fits ? 'Resolved' : 'PositionOutOfRange', codeItem }
}

export function isUnresolvedColumnState(state: AppendixColumnRefState): boolean {
  return state.status === 'Missing' || state.status === 'PositionOutOfRange'
}

export function isUnresolvedCellState(state: AppendixCellRefState): boolean {
  return state.status === 'Missing' || state.status === 'Mismatch'
}

// Columns pointing at a missing code item or position, and cells pointing at a missing or
// misfitting value. Empty cells are complete.
export function countUnresolved(
  columns: AppendixColumnDraft[],
  rows: AppendixRowDraft[],
  index: AppendixRefIndex
): number {
  let count = 0
  columns.forEach(column => {
    if (isUnresolvedColumnState(getColumnRefState(column, index))) count += 1
  })
  rows.forEach(row => columns.forEach(column => {
    if (isUnresolvedCellState(getCellRefState(column, row.cells[column.key], index))) count += 1
  }))
  return count
}

// "C16", or "018 position 1" when the column names a position range.
export function formatColumnRefCode(column: AppendixColumnRef, index: AppendixRefIndex): string {
  const code = column.codeItemKey ? index.codeItems.get(column.codeItemKey)?.code || '' : ''
  const start = column.codeItemPosition
  if (!code || start === null || start === undefined) return code
  const end = column.codeItemPositionEnd ?? start
  return end === start ? `${code} position ${start + 1}` : `${code} positions ${start + 1}–${end + 1}`
}

// -----------------------------
// Validation
// -----------------------------

// `allowEmpty` lets cells stay blank in a table that defines valid combinations. An empty code
// item cell is always allowed and complete.
export function validateCell(
  column: Pick<AppendixColumnDraft, 'dataType'> & AppendixColumnRef,
  value: string | null | undefined,
  index: AppendixRefIndex | null,
  allowEmpty = false,
  savedValue?: string
): string | null {
  const trimmed = (value ?? '').trim()

  if (column.dataType === 'CodeItem') {
    if (!trimmed) return null
    if (!column.codeItemKey || !index) return 'Choose which code item and position this column references.'

    const state = getCellRefState(column, trimmed, index)
    if (!isUnresolvedCellState(state)) return null
    // A reference that was already broken when loaded is counted as incomplete but can be saved.
    if (savedValue !== undefined && trimmed === savedValue.trim()) return null
    const code = index.codeItems.get(column.codeItemKey)?.code
    return code ? `Choose a value of code item ${code}.` : 'Choose another value.'
  }

  if (!trimmed) return allowEmpty ? null : EMPTY_CELL_ERROR

  switch (column.dataType) {
    case 'Number':
      return NUMBER_PATTERN.test(trimmed) ? null : NUMBER_CELL_ERROR
    case 'Boolean':
      return trimmed === 'true' || trimmed === 'false' ? null : BOOLEAN_CELL_ERROR
    default:
      return null
  }
}

export type AppendixColumnError = {
  field: 'label' | 'codeItem'
  message: string
}

// What a column points at, to tell a reference the author set from the one that was loaded.
export function columnRefSignature(column: Pick<AppendixColumnDraft, 'dataType'> & AppendixColumnRef): string {
  if (column.dataType !== 'CodeItem') return ''
  return JSON.stringify([column.codeItemKey ?? null, column.codeItemGroup ?? null, column.codeItemPosition ?? null, column.codeItemPositionEnd ?? column.codeItemPosition ?? null])
}

// Mirrors the server's save rules for references the author set or changed: a composite code item
// needs a subgroup position range its values fill, and a simple one takes no position. A column
// whose reference was loaded broken and left untouched is not an error: it counts as incomplete
// and the rest of the table can still be saved. `savedRefs` holds columnRefSignature by column key
// as loaded; the reference rules apply only with live targets.
export function validateColumns(
  columns: AppendixColumnDraft[],
  index?: AppendixRefIndex | null,
  savedRefs?: ReadonlyMap<string, string>
): Record<string, AppendixColumnError> {
  const errors: Record<string, AppendixColumnError> = {}
  const seenLabels = new Set<string>()

  columns.forEach(column => {
    const label = column.label.trim()
    if (!label) {
      errors[column.key] = { field: 'label', message: 'Every column needs a label.' }
      return
    }

    const normalizedLabel = label.toLowerCase()
    if (seenLabels.has(normalizedLabel)) {
      errors[column.key] = { field: 'label', message: `There is more than one column named ${label}.` }
      return
    }
    seenLabels.add(normalizedLabel)

    if (column.dataType !== 'CodeItem') return
    if (!column.codeItemKey) {
      errors[column.key] = { field: 'codeItem', message: `Choose which code item ${label} references.` }
      return
    }
    if (!index?.live || savedRefs?.get(column.key) === columnRefSignature(column)) return
    const state = getColumnRefState(column, index)
    if (state.status === 'Missing') {
      errors[column.key] = { field: 'codeItem', message: `Choose which code item ${label} references.` }
    } else if (state.status === 'PositionOutOfRange') {
      errors[column.key] = {
        field: 'codeItem',
        message: state.codeItem.composite
          ? `Choose which code item and position ${label} references.`
          : `Code item ${state.codeItem.code} has no positions to choose. Choose it without a position for ${label}.`,
      }
    }
  })

  return errors
}

export const VALID_COMBINATIONS_COLUMN_ERROR = 'A table that defines valid combinations needs a code item column.'

// -----------------------------
// Type changes and re-pointing
// -----------------------------

type ColumnShape = Pick<AppendixColumnDraft, 'dataType'> & Required<AppendixColumnRef>

type ConvertedCell = { value: string; cleared: boolean; clearedText: string }

function matchValueKey(text: string, candidates: AppendixCodeItemOption['values']): string | null {
  const exact = candidates.find(value => value.code === text)
  if (exact) return exact.key
  const lower = text.toLowerCase()
  const loose = candidates.filter(value => value.code.trim().toLowerCase() === lower)
  return loose.length === 1 ? loose[0].key : null
}

// Converts one cell between column shapes: value ids become the value's current text when a
// column stops holding references, text is matched to a value that fits the new column, and ids
// that no longer fit a re-pointed column are cleared. A broken reference is cleared, never turned
// back into the text it was picked with.
function convertCell(
  value: string | null | undefined,
  from: ColumnShape,
  to: ColumnShape,
  candidates: AppendixCodeItemOption['values'],
  index: AppendixRefIndex
): ConvertedCell {
  const current = (value ?? '').trim()
  if (!current) return { value: value ?? '', cleared: false, clearedText: '' }

  const fromRefs = columnHoldsRefs(from)
  const label = fromRefs ? index.values.get(current) : undefined
  const text = fromRefs ? (label ? cellText(label, from, index.live) : null) : current
  const display = text ?? ''
  const clear = (): ConvertedCell => ({ value: '', cleared: true, clearedText: display })
  const keep = (next: string): ConvertedCell => ({ value: next, cleared: false, clearedText: '' })

  if (columnHoldsRefs(to)) {
    if (fromRefs) return candidates.some(candidate => candidate.key === current) ? keep(current) : clear()
    const key = matchValueKey(current, candidates)
    return key ? keep(key) : clear()
  }

  if (text === null) return clear()
  // A code item column without a code item yet keeps the text until one is chosen.
  if (to.dataType === 'CodeItem' || to.dataType === 'Text' || to.dataType === 'String') return keep(text)
  return validateCell({ dataType: to.dataType }, text, null) === null ? keep(text) : clear()
}

function columnShape(column: AppendixColumnDraft): ColumnShape {
  return {
    dataType: column.dataType,
    codeItemKey: column.codeItemKey ?? null,
    codeItemGroup: column.codeItemGroup ?? null,
    codeItemPosition: column.codeItemPosition ?? null,
    codeItemPositionEnd: column.codeItemPositionEnd ?? null,
  }
}

function toShape(nextType: AppendixColumnDataType, nextCodeItem: AppendixCodeItemOption | null): ColumnShape {
  const { codeItemCode: _code, ...ref } = columnRefFromOption(nextType === 'CodeItem' ? nextCodeItem : null)
  return { dataType: nextType, ...ref }
}

function convertColumnCells(
  columns: AppendixColumnDraft[],
  rows: AppendixRowDraft[],
  columnKey: string,
  nextType: AppendixColumnDataType,
  nextCodeItem: AppendixCodeItemOption | null,
  index: AppendixRefIndex
): { from: ColumnShape; to: ColumnShape; cells: ConvertedCell[] } | null {
  const column = columns.find(item => item.key === columnKey)
  if (!column) return null
  const from = columnShape(column)
  const to = toShape(nextType, nextCodeItem)
  const candidates = nextType === 'CodeItem' && nextCodeItem && !nextCodeItem.positions?.length ? nextCodeItem.values : []
  return { from, to, cells: rows.map(row => convertCell(row.cells[columnKey], from, to, candidates, index)) }
}

// The text of the cells a type change would clear, for the confirmation ("such as 5 to 1").
// Broken references have no text to show but still count.
export function findCellsClearedByTypeChange(
  columns: AppendixColumnDraft[],
  rows: AppendixRowDraft[],
  columnKey: string,
  nextType: AppendixColumnDataType,
  nextCodeItem: AppendixCodeItemOption | null,
  index: AppendixRefIndex = EMPTY_REF_INDEX
): string[] {
  const converted = convertColumnCells(columns, rows, columnKey, nextType, nextCodeItem, index)
  return converted ? converted.cells.filter(cell => cell.cleared).map(cell => cell.clearedText) : []
}

export function countCellsClearedByTypeChange(
  columns: AppendixColumnDraft[],
  rows: AppendixRowDraft[],
  columnKey: string,
  nextType: AppendixColumnDataType,
  nextCodeItem: AppendixCodeItemOption | null,
  index: AppendixRefIndex = EMPTY_REF_INDEX
): number {
  return findCellsClearedByTypeChange(columns, rows, columnKey, nextType, nextCodeItem, index).length
}

export function applyColumnTypeChange(
  columns: AppendixColumnDraft[],
  rows: AppendixRowDraft[],
  columnKey: string,
  nextType: AppendixColumnDataType,
  nextCodeItem: AppendixCodeItemOption | null,
  index: AppendixRefIndex = EMPTY_REF_INDEX
): { columns: AppendixColumnDraft[]; rows: AppendixRowDraft[]; clearedCount: number } {
  const converted = convertColumnCells(columns, rows, columnKey, nextType, nextCodeItem, index)
  if (!converted) return { columns, rows, clearedCount: 0 }

  const ref = columnRefFromOption(nextType === 'CodeItem' ? nextCodeItem : null)
  const nextColumns = columns.map(item => item.key === columnKey
    ? { ...item, dataType: nextType, unit: columnHasUnit(nextType) ? item.unit : '', ...ref }
    : item)

  let clearedCount = 0
  const nextRows = rows.map((row, rowIndex) => {
    const cell = converted.cells[rowIndex]
    if (cell.cleared) clearedCount += 1
    if (cell.value === (row.cells[columnKey] ?? '')) return row
    return { ...row, cells: { ...row.cells, [columnKey]: cell.value } }
  })

  return { columns: nextColumns, rows: nextRows, clearedCount }
}

export function removeColumn(
  columns: AppendixColumnDraft[],
  rows: AppendixRowDraft[],
  columnKey: string
): { columns: AppendixColumnDraft[]; rows: AppendixRowDraft[]; removedCount: number } {
  let removedCount = 0

  const nextRows = rows.map(row => {
    const { [columnKey]: removedValue, ...cells } = row.cells
    if ((removedValue ?? '').trim()) removedCount += 1
    if (!row.cellMeta) return { ...row, cells }
    const { [columnKey]: _removedMeta, ...cellMeta } = row.cellMeta
    return { ...row, cells, cellMeta }
  })

  return {
    columns: columns.filter(column => column.key !== columnKey),
    rows: nextRows,
    removedCount,
  }
}

export function moveItem<T>(items: T[], fromIndex: number, toIndex: number): T[] {
  if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0 || fromIndex >= items.length || toIndex >= items.length) {
    return items
  }

  const next = [...items]
  const [moved] = next.splice(fromIndex, 1)
  next.splice(toIndex, 0, moved)
  return next
}

// -----------------------------
// Load and save
// -----------------------------

const APPENDIX_COLUMN_DATA_TYPES = new Set<string>([...APPENDIX_COLUMN_TYPE_OPTIONS.map(option => option.value), 'String', 'Boolean'])

// Builds the editor draft from a loaded appendix. Only the first table is edited; cells map to
// columns by column position. Code item cells hold the value id, never the stored text.
export function hydrateAppendixDraft(detail: AppendixDetail): {
  columns: AppendixColumnDraft[]
  rows: AppendixRowDraft[]
  meta: SavedAppendixTableMeta
} {
  const [table] = detail.tables
  const meta: SavedAppendixTableMeta = {
    description: detail.description || '',
    tableId: table?.id || null,
    tableDescription: table?.description || '',
    definesValidCombinations: detail.definesValidCombinations ?? false,
  }
  if (!table) return { columns: [], rows: [], meta }

  const sortedColumns = [...table.columns].sort((a, b) => a.position - b.position)
  const columns: AppendixColumnDraft[] = sortedColumns.map(column => {
    const dataType = column.dataType && APPENDIX_COLUMN_DATA_TYPES.has(column.dataType) ? column.dataType : 'Text'
    const isCodeItem = dataType === 'CodeItem'
    const position = isCodeItem ? column.codeItemPosition ?? null : null
    return {
      key: createDraftKey('column'),
      id: column.id,
      length: column.length,
      label: column.title || column.name || '',
      dataType,
      unit: column.unit || '',
      // The code as it reads now when the server resolved it; only sent back, never displayed.
      codeItemCode: isCodeItem ? column.codeItemRef?.code || column.codeItemData || '' : '',
      codeItemKey: isCodeItem ? column.codeItemKey ?? null : null,
      codeItemGroup: isCodeItem ? column.codeItemGroup ?? null : null,
      codeItemPosition: position,
      codeItemPositionEnd: position === null ? null : column.codeItemPositionEnd ?? position,
    }
  })
  const columnByPosition = new Map(sortedColumns.map((column, index) => [column.position, columns[index]]))

  const rows: AppendixRowDraft[] = [...table.rows]
    .sort((a, b) => a.position - b.position)
    .map(row => {
      const cells: Record<string, string> = Object.fromEntries(columns.map(column => [column.key, '']))
      const cellMeta: Record<string, AppendixCellMeta> = {}
      row.cells.forEach(cell => {
        const column = columnByPosition.get(cell.columnPosition)
        if (!column) return
        const value = column.dataType === 'CodeItem' ? cell.valueRefId ?? '' : cell.value ?? ''
        cells[column.key] = value
        cellMeta[column.key] = { id: cell.id, notes: cell.notes, savedValue: value }
      })
      return { key: createDraftKey('row'), id: row.id, cells, cellMeta }
    })

  return { columns, rows, meta }
}

export function buildAppendixPayload(
  name: string,
  columns: AppendixColumnDraft[],
  rows: AppendixRowDraft[],
  saved?: SavedAppendixTableMeta,
  definesValidCombinations = saved?.definesValidCombinations ?? false
): CreateAppendixPayload {
  const title = name.trim()
  // Creates send no ids; updates send the loaded ids, with null for anything added since.
  const withId = <T extends object>(id: string | null | undefined, body: T) => saved ? { id: id ?? null, ...body } : body

  return {
    title,
    description: saved?.description ?? '',
    definesValidCombinations,
    tables: [withId(saved?.tableId, {
      name: title,
      description: saved?.tableDescription ?? '',
      columns: columns.map((column, position) => {
        const label = column.label.trim()
        const unit = columnHasUnit(column.dataType) ? column.unit.trim() : ''
        const isCodeItem = column.dataType === 'CodeItem'
        const codeItemPosition = isCodeItem ? column.codeItemPosition ?? null : null
        return withId(column.id, {
          name: label,
          title: label,
          dataType: column.dataType,
          ...(saved ? { length: column.length ?? null } : {}),
          unit: unit || null,
          position,
          codeItemData: isCodeItem ? column.codeItemCode || null : null,
          codeItemKey: isCodeItem ? column.codeItemKey || null : null,
          codeItemGroup: isCodeItem ? column.codeItemGroup ?? null : null,
          codeItemPosition,
          codeItemPositionEnd: codeItemPosition === null ? null : column.codeItemPositionEnd ?? codeItemPosition,
        })
      }),
      rows: rows.map((row, position) => withId(row.id, {
        position,
        cells: columns.map((column, columnPosition) => {
          const meta = row.cellMeta?.[column.key]
          const value = (row.cells[column.key] ?? '').trim()
          const isCodeItem = column.dataType === 'CodeItem'
          return withId(meta?.id, {
            value: isCodeItem ? null : value,
            valueRefId: isCodeItem ? value || null : null,
            notes: meta?.notes ?? null,
            columnPosition,
          })
        }),
      })),
    })],
  }
}

// -----------------------------
// Formatting
// -----------------------------

export function formatColumnPillMeta(column: Pick<AppendixColumnDraft, 'dataType' | 'unit'>): string {
  const typeLabel = getColumnTypeLabel(column.dataType)
  const unit = columnHasUnit(column.dataType) ? column.unit.trim() : ''
  return unit ? `${typeLabel} - ${unit}` : typeLabel
}

// A code item column is headed by what it points at, read from the saved targets; a column whose
// code item no longer exists falls back to its label (never to the code it was saved with).
export function formatColumnHeader(column: Pick<AppendixColumnDraft, 'label' | 'dataType' | 'unit'> & Partial<AppendixColumnDraft>, fallback: string, codeItems?: AppendixCodeItemOption[]): string {
  if (column.dataType === 'CodeItem' && codeItems) {
    const target = column.codeItemKey || column.codeItemCode
      ? findCodeItemOption(codeItems, { ...column, codeItemCode: column.codeItemCode || '' })
      : null
    if (target) return formatCodeItemTarget(target, false)
  }
  const label = column.label.trim() || fallback
  const unit = columnHasUnit(column.dataType) ? column.unit.trim() : ''
  return unit ? `${label} - ${unit}` : label
}

export function formatCount(count: number, singular: string, plural = `${singular}s`): string {
  return `${count} ${count === 1 ? singular : plural}`
}

// "1 entry points at something that no longer exists" / "3 entries point at ...".
export function formatUnresolvedSummary(count: number): string {
  return `${formatCount(count, 'entry', 'entries')} ${count === 1 ? 'points' : 'point'} at something that no longer exists`
}

// Phrases describing values that don't fit a column type, e.g. "31 rows hold values that aren't numbers".
export function describeInvalidValues(
  dataType: AppendixColumnDataType,
  codeItemCode: string,
  count: number
): string {
  const single = count === 1
  switch (dataType) {
    case 'Number':
      return single ? 'a number' : 'numbers'
    case 'Boolean':
      return 'true or false'
    case 'CodeItem':
      return single ? `a value of code item ${codeItemCode}` : `values of code item ${codeItemCode}`
    default:
      return single ? `a ${getColumnTypeNoun(dataType)}` : `${getColumnTypeNoun(dataType)} values`
  }
}

export function formatValueExamples(values: string[], limit = 2): string {
  const distinct = Array.from(new Set(values.map(value => value.trim()).filter(Boolean))).slice(0, limit)
  return distinct.join(' and ')
}
